/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React, {
  ComponentPropsWithoutRef,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import cn from "classnames";
import { useOnClickOutside } from "usehooks-ts";

import { TChildren } from "constants/interfaces";

import { ChevronDown } from "components/icons";

import Button from "components/ui/Button/Button";

import s from "./Dropdown.module.css";

const INITIAL_MAX_HEIGHT = 135;

type ButtonProps = Partial<ComponentPropsWithoutRef<typeof Button>> & {
  text: React.ReactNode;
  hasChevron?: boolean;
};

interface DropDownInterface {
  children?: TChildren;
  pos: "left" | "right" | "full" | "auto" | "top";
  variant?: "dark" | "black" | "secondary";
  hasShadow?: true;
  listClassName?: string;
  containerClassName?: string;
  maxHeight?: number;
  minHeight?: number;
  overflowY?: "overflow-y-auto" | "overflow-y-hidden";
  disabled?: boolean;
  isOpen?: boolean;
  btnProps?: ButtonProps;
  hasButton?: boolean;
  onOutsideClick?: () => void;
  render?: any;
  avoidOverflow?: boolean;
}

const DropDown = ({
  children,
  pos = "left",
  variant = "dark",
  containerClassName,
  listClassName,
  hasButton = true,
  hasShadow,
  minHeight = 0,
  maxHeight = INITIAL_MAX_HEIGHT,
  disabled = false,
  overflowY = "overflow-y-auto",
  isOpen = false,
  btnProps = {
    text: "",
    variant: "select",
    size: "xl",
    hasChevron: true,
    onClick: () => {},
  },
  onOutsideClick,
  render = null,
  avoidOverflow = false,
}: DropDownInterface) => {
  const dropDownRef = useRef<HTMLDivElement>(null);
  const [isMenuDown, setMenuDown] = useState(isOpen);
  useOnClickOutside(dropDownRef, () => {
    if (isMenuDown) setMenuDown(false);
    if (onOutsideClick) onOutsideClick();
  });

  useEffect(() => {
    setMenuDown(isOpen);
  }, [isOpen]);

  const dropdownNode = dropDownRef?.current;
  // @ts-ignore
  const offsetParent = dropdownNode?.offsetParent?.offsetParent;

  const overFlowPosition = useMemo(() => {
    if (!avoidOverflow) return pos;
    let shouldOpenUp = false;
    if (offsetParent) {
      const offsetParentRect = offsetParent.getBoundingClientRect();
      const dropdownRect = dropdownNode.getBoundingClientRect();
      const bottom = offsetParentRect.bottom - dropdownRect.bottom;
      if (bottom - maxHeight < 0) {
        shouldOpenUp = true;
      }
    }

    return shouldOpenUp ? "top" : pos;
  }, [maxHeight, minHeight, offsetParent?.offsetHeight, isMenuDown]);

  const rootClass = cn(
    s.root,
    variant && [s[variant]],
    pos && [s[overFlowPosition]],
    hasShadow && s.hasShadow,
    listClassName,
    "z-[30]",
    overflowY && overflowY.startsWith("overflow")
      ? overflowY
      : `overflow-y-${overflowY}`,
  );

  const containerClass = cn(s.container, containerClassName);
  return (
    <div ref={dropDownRef} className={containerClass} test-id="Wu93eriK">
      {hasButton && (
        <Button
          type="button"
          disabled={disabled}
          size={btnProps.size || "xl"}
          variant={btnProps.variant || "select"}
          className={cn(btnProps.className || "min-h-[45px]", {
            "bg-[#252525]": isMenuDown,
          })}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            setMenuDown(!isMenuDown);
            if (btnProps.onClick) btnProps.onClick(e);
          }}
          test-id="Cwz4uoDh"
        >
          {btnProps.text}
          {btnProps.hasChevron !== false && (
            <ChevronDown className={s.caretDown} test-id="zU2hh-sX" />
          )}
        </Button>
      )}

      <div
        className={cn(rootClass, "list-unstyled z-[2] transition-transform")}
        style={
          isMenuDown
            ? {
                maxHeight,
                minHeight,
              }
            : { maxHeight: 0, border: "none" }
        }
      >
        {render
          ? render(() => setMenuDown(false))
          : typeof children === "function"
            ? children(() => setMenuDown(false))
            : children}
      </div>
    </div>
  );
};

export default DropDown;
